import React, { useState, useEffect } from "react";
import { Platform } from "react-native";
import { useMicrophoneDevices } from "../../../hooks/useMicrophoneDevices";
import { saveMicrophonePreference, setSystemDefaultMicrophone } from "../../../utils/microphoneStorage";
import MicrophoneSelectorDropdown from "../../../components/assessment/MicrophoneSelectorDropdown";

interface CompactMicrophoneSelectorProps {
  selectedDeviceId: string;
  onDeviceSelect: (deviceId: string) => void;
  disabled?: boolean;
}

const CompactMicrophoneSelector: React.FC<CompactMicrophoneSelectorProps> = ({
  selectedDeviceId,
  onDeviceSelect,
  disabled = false,
}) => {
  const { devices, selectedDeviceId: hookSelectedDeviceId, isLoading, error, selectDevice } = useMicrophoneDevices();
  const [isSettingDefault, setIsSettingDefault] = useState(false);
  const [isAudioActive, setIsAudioActive] = useState(false);
  const isMountedRef = React.useRef(false);

  const effectiveSelectedDeviceId = hookSelectedDeviceId || selectedDeviceId;

  useEffect(() => {
    isMountedRef.current = true;
  }, []);

  const handleDeviceSelect = async (deviceId: string) => {
    setIsSettingDefault(true);
    try {
      await selectDevice(deviceId);
      await saveMicrophonePreference(deviceId);
      await setSystemDefaultMicrophone(deviceId);

      if (typeof onDeviceSelect === "function") {
        onDeviceSelect(deviceId);
      }
      setIsAudioActive(true);
    } finally {
      setIsSettingDefault(false);
    }
  };

  useEffect(() => {
    if (effectiveSelectedDeviceId && devices.length > 0 && !isSettingDefault) {
      // Small delay to ensure the device is properly set
      const timer = setTimeout(() => {
        setIsAudioActive(true);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [effectiveSelectedDeviceId, devices.length, isSettingDefault]);

  useEffect(() => {
    if (
      isMountedRef.current &&
      hookSelectedDeviceId &&
      hookSelectedDeviceId !== selectedDeviceId &&
      typeof onDeviceSelect === "function" &&
      hookSelectedDeviceId !== "default"
    ) {
      onDeviceSelect(hookSelectedDeviceId);
    }
  }, [hookSelectedDeviceId, selectedDeviceId, onDeviceSelect]);

  if (Platform.OS !== "web") {
    return null;
  }

  return (
    <MicrophoneSelectorDropdown
      devices={devices}
      selectedDeviceId={effectiveSelectedDeviceId}
      onDeviceSelect={handleDeviceSelect}
      disabled={disabled}
      isSettingDefault={isSettingDefault}
      isAudioActive={isAudioActive}
      isCompact={true}
      isLoading={isLoading}
      error={error}
    />
  );
};

export default CompactMicrophoneSelector;
